import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Play } from 'lucide-react';
import { SplitText } from '../../ui/SplitText/SplitText';
import { Button } from '../../ui/Button/Button';
import { Magnetic } from '../../ui/Magnetic/Magnetic';
import './Hero.css';

type Clip = {
  id: string;
  title: string;
  location: string;
  poster: string;
};

const CLIPS: Clip[] = [
  {
    id: 'musical-event',
    title: 'Live Concert — Musical Event',
    location: 'London',
    poster: '/images/musical_event.png',
  },
  {
    id: 'techno-event',
    title: 'Raveland — Techno Event',
    location: 'Berlin',
    poster: '/images/techno_event.png',
  },
  {
    id: 'marriage-party',
    title: 'Luxury Marriage Party',
    location: 'Paris',
    poster: '/images/marriage_party.png',
  },
];

const CLIP_DURATION = 8000;

export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Auto-cycle clips
  useEffect(() => {
    const t = setTimeout(() => {
      setActive((i) => (i + 1) % CLIPS.length);
    }, CLIP_DURATION);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section ref={ref} id="top" className="hero">
      {/* Cinematic video bed — stacked clips, crossfade between */}
      <motion.div className="hero__video-wrap" style={{ scale: videoScale }}>
        {CLIPS.map((clip, i) => (
          <div
            key={clip.id}
            className={`hero__clip ${i === active ? 'hero__clip--active' : ''}`}
          >
            <img className="hero__clip-poster" src={clip.poster} alt="" />
          </div>
        ))}
      </motion.div>

      {/* Overlays removed for clearer images */}

      {/* Reel badge — updates per clip */}
      <motion.div
        className="hero__reel-badge"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="hero__reel-dot" />
        <span className="hero__reel-now">
          Reel 2025
          <span className="hero__reel-count">
            {' '}0{active + 1}/0{CLIPS.length}
          </span>
        </span>
        <AnimatePresence mode="wait">
          <motion.span
            key={CLIPS[active].id}
            className="hero__reel-meta"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
          >
            {CLIPS[active].title} · {CLIPS[active].location}
          </motion.span>
        </AnimatePresence>
      </motion.div>

      {/* Progress strip — visualizes the auto-cycle */}
      <div className="hero__progress">
        {CLIPS.map((c, i) => (
          <div key={c.id} className="hero__progress-track">
            {i === active && (
              <motion.div
                className="hero__progress-bar"
                key={`bar-${active}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: CLIP_DURATION / 1000, ease: 'linear' }}
              />
            )}
            {i < active && <div className="hero__progress-bar hero__progress-bar--done" />}
          </div>
        ))}
      </div>

      <div className="container hero__inner">

        <motion.h1 className="hero__title display-xl" style={{ opacity }}>
          <span className="hero__title-line">
            <SplitText text="Events that" stagger={0.03} />
          </span>
          <span className="hero__title-line">
            <SplitText text="feel" delay={0.3} stagger={0.04} />{' '}
            <span className="italic-accent">
              <SplitText text="inevitable." delay={0.45} stagger={0.04} />
            </span>
          </span>
        </motion.h1>

        <div className="hero__row">
          <motion.p
            className="hero__lede"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            Luminar is a global events studio producing concerts, conferences, ceremonies and
            cultural spectacle for the world's most ambitious brands and families.
          </motion.p>

          <motion.div
            className="hero__actions"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Button href="/book-tickets">Book Tickets</Button>
            <Magnetic strength={0.25}>
              <a href="#showcase" className="hero__play">
                <span className="hero__play-disc">
                  <Play size={16} fill="currentColor" strokeWidth={0} />
                </span>
                <span className="hero__play-label">Watch the reel</span>
              </a>
            </Magnetic>
          </motion.div>
        </div>

        <motion.div
          className="hero__scroll"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <span>Scroll</span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ArrowDown size={16} strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
